import { lazy } from "react";
import { Navigate } from "react-router-dom";
import { ProtectedRoute } from "./lib/auth";
import Dashboard from "./component/Dashboard/Dashboard";

// Public marketing / legal pages (served at the root domain, same bundle as the admin)
const PrivacyPolicyNew = lazy(() => import("./component/Premika/PrivacyPolicyNew"));
const Home = lazy(() => import("./component/Premika/Home"));
const Premikaabout = lazy(() => import("./component/Premika/Premikaabout"));
const Premikatopbar = lazy(() => import("./component/Premika/Premikatopbar"));
const RefundPolicy = lazy(() => import("./component/Premika/RefundPolicy"));
const Term = lazy(() => import("./component/Premika/Term"));
const ChildSafety = lazy(() => import("./component/Premika/ChildSafety"));
const DeleteDetails = lazy(() => import("./component/Premika/DeleteDetails"));
const WalletBalance = lazy(
  () => import("./component/Premika/Transaction/WalletBalance")
);
const Payment = lazy(() => import("./component/Premika/Transaction/Payment"));
const Successfully = lazy(
  () => import("./component/Premika/Transaction/Successfully")
);
const PlanSection = lazy(
  () => import("./component/Premika/Price Plan/PlanSection")
);
const Contact = lazy(() => import("./component/Premika/Contact"));
const ShippingPolicy = lazy(() => import("./component/Premika/ShippingPolicy"));
const PlayStore = lazy(() => import("./component/PlayStore"));

// Admin pages (only the ones backed by real backend routes)
const Credentials = lazy(() => import("./component/Credentials/Credentials"));
const CredentialsForm = lazy(
  () => import("./component/Credentials/CredentialsForm")
);
const CredentialsShow = lazy(
  () => import("./component/Credentials/CredentialsShow")
);
const Recharge = lazy(() => import("./component/Recharge/recharge"));
const RechargeBody = lazy(() => import("./component/Recharge/rechargeBody"));
const RechargeEdit = lazy(
  () => import("./component/Recharge/Recharge Edit/rechargeedit")
);
const Login = lazy(() => import("./component/Auth/login"));
const Password = lazy(() => import("./component/Auth/Password"));
const UserTableBody = lazy(() => import("./component/UserData/UserTablebody"));
const UserShow = lazy(() => import("./component/User/UserShow"));
const Sidebar = lazy(() => import("./component/Global/sidebar"));

const wrapWithProtection = (
  element,
  role = null,
  // `permission` arg kept for call-site compatibility but intentionally NOT
  // enforced: the astrology-era permission strings are unreliable and could
  // bounce a valid admin to /unauthorized. Access is role-gated, which is
  // sufficient for this single-admin tool.
  // eslint-disable-next-line no-unused-vars
  permission,
  redirectTo = "/"
) => {
  if (!role) return element;
  return (
    <ProtectedRoute role={role} redirectTo={redirectTo}>
      {element}
    </ProtectedRoute>
  );
};

export const RoutesArray = [
  // 🌐 Public Routes

  {
    path: "/credentials",
    element: <Navigate to="/credentials/login" />,
  },
  {
    path: "/",
    element: <Premikatopbar />,
    children: [
      {
        index: true,
        title: "Premika - Home",
        description: "Welcome to Premika",
        element: <Home />,
      },
      {
        path: "about",
        title: "Premika - About",
        description: "Learn more about Premika",
        element: <Premikaabout />,
      },
      {
        path: "refund_policy",
        title: "Premika - Refund Policy",
        description: "Understand our refund policy",
        element: <RefundPolicy />,
      },
      {
        path: "privacy_policy",
        title: "Premika - Privacy Policy",
        description: "Read our privacy policy",
        element: <PrivacyPolicyNew />,
      },
      {
        path: "terms_and_conditions",
        title: "Premika - Terms and Conditions",
        description: "Read our terms and conditions",
        element: <Term />,
      },
      {
        path: "child_safety",
        title: "Premika - Child Safety",
        description: "Learn about child safety",
        element: <ChildSafety />,
      },
      {
        path: "delete_account",
        title: "Premika - Delete Account",
        description: "Delete your account",
        element: <DeleteDetails />,
      },
      {
        path: "pricing_plan",
        title: "Premika - Pricing Plan",
        description: "View our pricing plans",
        element: <PlanSection />,
      },
      {
        path: "contact_us",
        title: "Premika - Contact Us",
        description: "Get in touch with us",
        element: <Contact />,
      },
      {
        path: "shipping_policy",
        title: "Premika - Shipping Policy",
        description: "Read our shipping policy",
        element: <ShippingPolicy />,
      },
      {
        path: "app/*",
        title: "Premika - Play Store",
        description: "Visit our Play Store page",
        element: <PlayStore />,
      },
      {
        path: "my_wallet",
        title: "Premika - My Wallet",
        description: "View your wallet balance",
        element: <WalletBalance />,
      },
      {
        path: "payment",
        title: "Premika - Payment",
        description: "Make a payment",
        element: <Payment />,
      },
      {
        path: "payment-success",
        title: "Premika - Payment Success",
        description: "Payment was successful",
        element: <Successfully />,
      },
    ],
  },
  {
    path: "credentials/login",
    title: "Premika - Login",
    description: "Login to your account",
    element: <Login />,
  },
  {
    path: "credentials/password",
    title: "Premika - Forgot Password",
    description: "Reset your password",
    element: <Password />,
  },

  {
    path: "/admin",
    element: <Navigate to="/admin/user" />,
  },
  // 🔐 Admin Protected Routes — only pages backed by real backend endpoints
  {
    path: "/admin",
    element: wrapWithProtection(<Sidebar />, [
      "admin",
      "super_admin",
      "sub_admin",
    ]),
    children: [
      {
        path: "dashboard",
        title: "Dashboard",
        description: "Welcome to Premika, Admin Panel",
        element: wrapWithProtection(
          <Dashboard />,
          ["admin", "super_admin", "sub_admin"],
          "DASHBOARD"
        ),
      },
      {
        path: "user",
        title: "Users",
        description: "Users Page",
        element: wrapWithProtection(
          <UserTableBody />,
          ["admin", "super_admin", "sub_admin"],
          "USER"
        ),
      },
      {
        path: "user/user_detail",
        title: "User Details",
        description: "User Details Page",
        element: wrapWithProtection(
          <UserShow />,
          ["admin", "super_admin", "sub_admin"],
          "USER"
        ),
      },
      {
        path: "plan",
        title: "Plan",
        description: "plan Page",
        element: wrapWithProtection(
          <Recharge />,
          ["admin", "super_admin", "sub_admin"],
          "PLANS"
        ),
      },
      {
        path: "plan/create_plan",
        title: "Creating plan",
        description: "plan Creation Page",
        element: wrapWithProtection(
          <RechargeBody />,
          ["admin", "super_admin", "sub_admin"],
          "PLANS"
        ),
      },
      {
        path: "plan/plan_detail",
        title: "plan Details",
        description: "plan Details Page",
        element: wrapWithProtection(
          <RechargeEdit />,
          ["admin", "super_admin", "sub_admin"],
          "PLANS"
        ),
      },
      {
        path: "credentials",
        title: "Credentials",
        description: "Credentials Page",
        element: wrapWithProtection(
          <Credentials />,
          ["admin", "super_admin", "sub_admin"],
          "CREDENTIALS"
        ),
      },
      {
        path: "credentials/create_credentials",
        title: "Creating Credentials",
        description: "Credentials Creation Page",
        element: wrapWithProtection(
          <CredentialsForm />,
          ["admin", "super_admin", "sub_admin"],
          "CREDENTIALS"
        ),
      },
      {
        path: "credentials/credentials_details",
        title: "Credentials Details",
        description: "Credentials Details Page",
        element: wrapWithProtection(
          <CredentialsShow />,
          ["admin", "super_admin", "sub_admin"],
          "CREDENTIALS"
        ),
      },
      {
        path: "*",
        element: <Navigate to="/admin/user" />,
      },
    ],
  },

  // 🔁 Catch-all route
  {
    path: "*",
    element: <Navigate to="/" />,
  },
];
